/**
 * Manifest review's loading, failure and refusal states: the shared Tide
 * StateBlock (src/components/tide/StateBlock.tsx; workspace density from
 * src/styles/tide/workspace.css) carrying the review's own words.
 *
 *   loading   static skeleton rows, `role="status"` with the sentence for
 *             assistive technology only, no aria-busy. No shimmer loop.
 *   refusal   the gate, in words (withheld tone).
 *   failure   nothing was loaded (danger tone), with Try again. A failure is
 *             never drawn as an empty list.
 */
import React from 'react';
import { StateBlock as TideState } from '../../components/tide';

/** `failure` marks a server that was not reached, failed, or answered without the data; otherwise it is a refusal at a named gate. */
export type Refusal = { title: string; body: string; failure?: boolean; status?: number };

/** A read that never reached eMerge (no status and no code), or threw before it could. */
export const UNREACHED: Refusal = { title: 'Server not reached', body: 'Could not reach the server, so nothing here is current.', failure: true };

/** Unreached or 5xx, told by status before any code is read. Null for a refusal or a server sentence. */
export function failureFor(r: { status?: number; code?: string | null }): Refusal | null {
  if (r.status === undefined && !r.code) return UNREACHED;
  if (typeof r.status === 'number' && r.status >= 500) {
    return { title: 'Server error', body: 'The server failed while loading this. This is not a permissions problem.', failure: true, status: r.status };
  }
  return null;
}

/**
 * The first-load skeleton. `announce` false for a second one beside it, so a
 * screen reader hears one; `bare` inside a card that is already drawn.
 */
export function MfSkeleton({ announce = true, bare = false }: { announce?: boolean; bare?: boolean }) {
  const block = <TideState kind="loading" shape="table" label="Loading…" busy={false} srOnlyLabel announce={announce} />;
  return bare ? block : <div className="mf-card">{block}</div>;
}

/** A refused or failed read of the morning or of one building. */
export function MfStateBlock({ refusal, retry }: { refusal: Refusal; retry?: () => void }) {
  return (
    <div className="mf-card mf-state">
      <TideState kind="error" heading={refusal.title} headingLevel={2} message={refusal.body}
        status={refusal.status} tone={refusal.failure ? 'danger' : 'withheld'} onRetry={retry} />
    </div>
  );
}

/** A desk's list did not load. The server's own sentence, then Try again; never "nobody is waiting". */
export function MfLoadFailure({ message, retry }: { message: string; retry: () => void }) {
  return (
    <section className="mf-card mf-state">
      <TideState kind="error" heading="Not loaded" headingLevel={2} message={message} onRetry={retry} />
    </section>
  );
}
